import useTranslation from 'customHooks/translate';
import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Space,
  Table,
  message,
  Skeleton,
  Spin,
  Popover,
  Popconfirm,
} from 'antd';
import { ColumnsType } from 'antd/lib/table';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import { getExpensesList, cancelCost } from 'services/invoices';
import { ExpensesDataType } from 'types';
import { EXPENSES } from 'events/types';
import { EditDeleteIcon, LoadingIndicator, TableWrapper } from 'styled';
import { PAGE_URL_KEY, EXPENSES_PAGE_URL } from 'consts';

const Expenses = () => {
  const translation = useTranslation();
  const queryClient = useQueryClient();

  // Infinite scroll related states
  const [pageSize] = useState(10);
  const [pageIndex, setPageIndex] = useState(0);
  const [expensesData, setExpensesData] = useState<ExpensesDataType[]>([]);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // Refs for scroll container and loading states
  const tableContainerRef = useRef<HTMLDivElement>(null);
  const loadingRef = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef(false);

  const setPageUrlForRedirect = () => {
    localStorage.setItem(PAGE_URL_KEY, EXPENSES_PAGE_URL);
  };

  // Function to fetch expenses data
  const fetchExpensesData = useCallback(
    async (currentPage: number, isNewRequest = false) => {
      if (isFetchingRef.current) return null;

      isFetchingRef.current = true;
      setIsLoadingMore(true);

      try {
        const response = await getExpensesList(pageSize, currentPage);
        const data = response.data;

        // Ensure we have data to work with
        if (data?.data?.length) {
          const formattedData = data.data.map((item: any) => ({
            ...item,
            key: item.id,
          }));

          // Set data based on whether this is a new request or not
          if (isNewRequest) {
            setExpensesData(formattedData);
          } else {
            setExpensesData((prev) => [...prev, ...formattedData]);
          }

          // Check if we have more data to load
          const newTotalItems =
            (isNewRequest ? 0 : expensesData.length) + formattedData.length;
          setHasMoreData(newTotalItems < data.totalRecord);

          return { data: formattedData, totalRecord: data.totalRecord };
        }

        setHasMoreData(false);
        return null;
      } catch {
        return null;
      } finally {
        setIsLoadingMore(false);
        isFetchingRef.current = false;
      }
    },
    [pageSize, expensesData.length]
  );

  // Initial data fetch with React Query
  const { status, isLoading } = useQuery({
    queryKey: [EXPENSES, pageSize, pageIndex],
    queryFn: () => fetchExpensesData(pageIndex, pageIndex === 0),
    keepPreviousData: true,
    refetchOnMount: 'always',
  });

  // Reset data function
  const resetData = () => {
    setExpensesData([]);
    setPageIndex(0);
    setHasMoreData(true);
  };

  const handleCancelInvoice = async (invoiceId) => {
    try {
      await cancelCost(invoiceId);
      message.success(translation.messages.successCancel);
      // Reset data and reload
      resetData();
      fetchExpensesData(0, true);
      queryClient.invalidateQueries(['incomes']);
    } catch (error) {
      message.error('Error cancelling invoice. Please try again later.');
    }
  };

  // Handle table scroll
  const handleTableScroll = useCallback(() => {
    if (
      !tableContainerRef.current ||
      !loadingRef.current ||
      isFetchingRef.current ||
      !hasMoreData
    )
      return;

    const containerRect = tableContainerRef.current.getBoundingClientRect();
    const loadingRect = loadingRef.current.getBoundingClientRect();

    // Check if the loading element is visible
    if (loadingRect.top <= containerRect.bottom + 100) {
      const nextPage = pageIndex + 1;
      setPageIndex(nextPage);
      fetchExpensesData(nextPage);
    }
  }, [pageIndex, hasMoreData, fetchExpensesData]);

  // Set up scroll event listener
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    if (tableContainer) {
      tableContainer.addEventListener('scroll', handleTableScroll);
      return () => {
        tableContainer.removeEventListener('scroll', handleTableScroll);
      };
    }
  }, [handleTableScroll]);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const columns: ColumnsType<ExpensesDataType> = [
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.expenses.Table_Name}
        </span>
      ),
      dataIndex: 'partnerName',
      key: 'partnerName',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.expenses.Table_JobNumb}
        </span>
      ),
      dataIndex: 'jobNumber',
      key: 'jobNumber',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.expenses.Table_Manager}
        </span>
      ),
      dataIndex: 'manager',
      key: 'manager',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.expenses.Table_Currency}
        </span>
      ),
      dataIndex: 'currency',
      key: 'currency',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.expenses.Table_ExcRate}
        </span>
      ),
      dataIndex: 'exchangeRate',
      key: 'exchangeRate',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.expenses.Table_ContNum}
        </span>
      ),
      dataIndex: 'contractNumber',
      key: 'contractNumber',
      align: 'center',
      width: 100,
    },
    {
      title: (
        <span className="text-[16px] font-medium text-[#466afb]">
          {translation.action.Action}
        </span>
      ),
      dataIndex: 'actions',
      key: 'actions',
      align: 'center',
      width: 100,
      render: (_, record) =>
        isMobile ? (
          <Popover
            placement="bottom"
            trigger="click"
            content={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '20px',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                <Link
                  onClick={setPageUrlForRedirect}
                  to={`/cost/${record.invoiceId}/detail`}
                  state={record}
                >
                  <div style={{ cursor: 'pointer' }}>
                    <EditDeleteIcon src="assets/edit-icon.svg" alt="Edit" />
                  </div>
                </Link>
                <Popconfirm
                  title={translation.DeleteItem}
                  onConfirm={() => handleCancelInvoice(record.invoiceId)}
                  okText="Yes"
                  placement="left"
                  cancelText="No"
                >
                  <EditDeleteIcon src="assets/delete-icon.svg" alt="Delete" />
                </Popconfirm>
              </div>
            }
          >
            <MoreVertIcon
              style={{
                fontSize: '29px',
                border: '1px solid #292d32bf',
                borderRadius: '50%',
                padding: '4px',
              }}
            />
          </Popover>
        ) : (
          <Space size="middle">
            <Link
              onClick={setPageUrlForRedirect}
              to={`/cost/${record.invoiceId}/detail`}
              state={record}
            >
              <div style={{ cursor: 'pointer' }}>
                <img className="edit" src="assets/edit-icon.svg" alt="" />
              </div>
            </Link>
            <Popconfirm
              title={translation.DeleteItem}
              onConfirm={() => handleCancelInvoice(record.invoiceId)}
              okText={<span style={{ color: 'black' }}>Yes</span>}
              cancelText={<span style={{ color: 'black' }}>No</span>}
            >
              <div style={{ cursor: 'pointer' }}>
                <img className="delete" src="assets/delete-icon.svg" alt="" />
              </div>
            </Popconfirm>
          </Space>
        ),
    },
  ];

  return (
    <>
      {status === 'loading' && expensesData.length === 0 ? (
        <Skeleton active />
      ) : (
        <TableWrapper ref={tableContainerRef}>
          <Table
            sticky={{
              offsetHeader: 0,
            }}
            scroll={{ x: 'max-content' }}
            dataSource={expensesData}
            columns={columns}
            loading={isLoading && expensesData.length === 0}
            pagination={false}
          />

          <LoadingIndicator ref={loadingRef}>
            {isLoadingMore && <Spin size="default" />}
            {!hasMoreData && expensesData.length > 0 && (
              <div style={{ color: '#999', marginTop: '8px' }}>
                {translation.messages.allData}
              </div>
            )}
          </LoadingIndicator>
        </TableWrapper>
      )}
    </>
  );
};

export default Expenses;
